import {HttpErrorResponse} from '@angular/common/http';
import {animate, group, query, state, style, transition, trigger} from '@angular/animations';
import {ChangeDetectionStrategy, Component, HostBinding, HostListener, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Store, select} from '@ngrx/store';
import {BehaviorSubject, Observable, Subject, Subscription, timer, withLatestFrom} from 'rxjs';
import {filter, take} from 'rxjs/operators';
import {Project} from 'src/app/core/model/project';
import {RewardCoin} from 'src/app/core/model/reward';
import {CurrentSkedTask} from 'src/app/core/model/task';
import {User} from 'src/app/core/model/user';
import {TitleService} from 'src/app/core/page/title.service';
import {GetActiveUserAction} from 'src/app/core/store/active-user/active-user.action';
import {selectActiveUser} from 'src/app/core/store/active-user/active-user.selector';
import {
  CurrentSkedChangeTaskStatusAction,
  GetCurrentSkedAction,
  GetCurrentSkedProjectAction,
  UpdateSkedContextAction,
} from 'src/app/core/store/current-sked/current-sked.action';
import {
  selectCurrentSked,
  selectCurrentSkedContext,
  selectSkedProjects,
} from 'src/app/core/store/current-sked/current-sked.selector';
import {GetLocationsAction} from 'src/app/core/store/locations/locations.action';
import {selectLocationsMap} from 'src/app/core/store/locations/locations.selector';
import {GetProgramsAction} from 'src/app/core/store/programs/programs.action';
import {selectProgramsMap} from 'src/app/core/store/programs/programs.selector';
import {GetProjectsAction} from 'src/app/core/store/projects/projects.action';
import {selectProjectsMap} from 'src/app/core/store/projects/projects.selector';
import {GetCoinTypesAction} from 'src/app/core/store/rewards/rewards.action';
import {selectCoinTypes} from 'src/app/core/store/rewards/rewards.selector';
import {Location} from 'src/app/locations/shared/model/location';
import {Program} from 'src/app/programs/shared/model/program';
import {MessageService} from 'src/app/services/message.service';
import {getNowSecondsOffset} from 'src/app/shared/utils/get-now-seconds-offset';
import {RolePermissionOptions} from '../../core/model/role';
import {CurrentSkedService} from '../shared/current-sked.service';
import {Sked, SkedContext} from '../shared/model/sked';
import {TaskStatusActions} from '../shared/task-status-actions';
import {TaskStatuses} from '../shared/task-statuses';

const PAGE_SLIDE_DURATION = '200ms ease-out';

@Component({
  selector: 'current-sked-task-list',
  templateUrl: './current-sked-task-list.component.html',
  styleUrls: ['./current-sked-task-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('pageSlide', [
      state('enter', style({transform: 'translateX(0)', opacity: 1})),
      state('leave', style({transform: 'translateX(100%)', opacity: 0})),
      state('none', style({transform: 'translateX(0)', opacity: 1})),
      transition('void => enter', [
        style({transform: 'translateX(100%)', opacity: 0}),
        animate(PAGE_SLIDE_DURATION, style({transform: 'translateX(0)', opacity: 1})),
      ]),
      transition('void => none', []),
      transition('* => leave', animate(PAGE_SLIDE_DURATION)),
    ]),
    trigger('tabContentSlide', [
      transition(':increment', [
        group([
          query(
            ':leave',
            [
              style({position: 'absolute', width: '100%', opacity: 1, transform: 'translateX(0)'}),
              animate('250ms ease', style({opacity: 0, transform: 'translateX(-100%)'})),
            ],
            {optional: true}
          ),
          query(
            ':enter',
            [
              style({opacity: 0, transform: 'translateX(100%)'}),
              animate('250ms ease', style({opacity: 1, transform: 'translateX(0)'})),
            ],
            {optional: true}
          ),
        ]),
      ]),
      transition(':decrement', [
        group([
          query(
            ':leave',
            [
              style({position: 'absolute', width: '100%', opacity: 1, transform: 'translateX(0)'}),
              animate('250ms ease', style({opacity: 0, transform: 'translateX(100%)'})),
            ],
            {optional: true}
          ),
          query(
            ':enter',
            [
              style({opacity: 0, transform: 'translateX(-100%)'}),
              animate('250ms ease', style({opacity: 1, transform: 'translateX(0)'})),
            ],
            {optional: true}
          ),
        ]),
      ]),
    ]),
  ],
})
export class CurrentSkedTaskListComponent implements OnInit, OnDestroy {
  @HostBinding('class') hostClasses = 'oph-feature-layout';
  @HostBinding('@pageSlide') animationState: 'enter' | 'leave' | 'none' = 'none';

  subscriptions = new Subscription();
  getCurrentSkedDataInterval: Subscription;
  private destroy$ = new Subject<void>();

  user$: Observable<User> = this.store$.select(selectActiveUser);
  currentSked$: Observable<Record<string, Sked>> = this.store$.select(selectCurrentSked);
  skedProjectsMap$: Observable<Record<string, Record<string, Sked>>> = this.store$.select(selectSkedProjects);
  projectsMap$: Observable<Record<string, Project>> = this.store$.pipe(select(selectProjectsMap));
  programsMap$: Observable<Record<string, Program>> = this.store$.pipe(select(selectProgramsMap));
  locationsMap$: Observable<Record<string, Location>> = this.store$.pipe(select(selectLocationsMap));
  coinTypes$: Observable<RewardCoin[]> = this.store$.pipe(select(selectCoinTypes));
  skedContext$: Observable<SkedContext> = this.store$.pipe(select(selectCurrentSkedContext));

  mainLoading$ = new BehaviorSubject<boolean>(true);
  headerLoading$ = new BehaviorSubject<boolean>(false);
  screenWidth$ = new BehaviorSubject<number>(null);
  loadingTaskAction$ = new BehaviorSubject<boolean>(false);
  allTasks$ = new BehaviorSubject<CurrentSkedTask[]>([]);
  activeTasks$ = new BehaviorSubject<CurrentSkedTask[]>([]); // tasks that are currently in progress
  filteredTasks$ = new BehaviorSubject<CurrentSkedTask[]>([]);

  getFrequency = 60; // in seconds
  rolePermissionOptions = RolePermissionOptions;
  filterTabIndex = 0;

  filterButtons = [
    {value: 'today', label: 'Today'},
    {value: 'expired', label: 'Expired'},
  ];

  @HostListener('window:resize')
  onWindowResize() {
    this.screenWidth$.next(window.innerWidth);
  }

  constructor(
    private titleService: TitleService,
    private messageService: MessageService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private store$: Store,
    private currentSkedService: CurrentSkedService
  ) {
    const nav = this.router.getCurrentNavigation();
    this.animationState = nav?.extras?.state?.['fromInitialSelection'] ? 'enter' : 'none';
  }

  ngOnInit() {
    this.titleService.setPageTitle('Current Sked');
    this.store$.dispatch(new GetCoinTypesAction({}));
    this.screenWidth$.next(window.innerWidth);

    this.initSkedContextFromRoute();
    this.subscriptions.add(this.subscribeToActiveUser());
    this.subscriptions.add(this.subscribeToTasks());
    this.subscriptions.add(this.subscribeToSkedContext());
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.unsubscribeFromGetCurrentSkedInterval();
    this.subscriptions.unsubscribe();

    // Complete all BehaviorSubjects
    this.mainLoading$.complete();
    this.headerLoading$.complete();
    this.screenWidth$.complete();
    this.loadingTaskAction$.complete();
    this.allTasks$.complete();
    this.activeTasks$.complete();
    this.filteredTasks$.complete();
  }

  subscribeToSkedContext(): Subscription {
    return this.skedContext$.pipe(filter(s => !!s)).subscribe(skedContext => {
      if ((skedContext.type === 'project' && !skedContext.projectId) || skedContext.preventGetSkedData) {
        return;
      }
      this.getCurrentSkedData();
    });
  }

  getCurrentSkedData() {
    this.skedContext$
      .pipe(take(1))
      .pipe(withLatestFrom(this.currentSked$, this.skedProjectsMap$))
      .subscribe(([skedContext, currentSked, skedProjectsMap]) => {
        if (skedContext.type === 'project') {
          this.handleProjectSelected(skedContext, skedProjectsMap);
          this.getCurrentSkedProject(skedContext.projectId, skedContext.filter === 'expired');
        }
        if (skedContext.type === 'sked') {
          this.handleSkedSelected(currentSked);
          this.getCurrentSked(skedContext.filter === 'expired');
        }
      });
    this.store$.dispatch(new GetProgramsAction({}));
    this.store$.dispatch(new GetLocationsAction({}));
    this.store$.dispatch(new GetProjectsAction({}));
    this.subscribeToGetCurrentSkedInterval();
  }

  handleSkedSelected(currentSkedMap: Record<string, Sked>) {
    this.skedContext$.pipe(take(1)).subscribe(skedContext => {
      const filterKey = skedContext.filter === 'expired' ? 'expired' : 'today';
      const currentSked = currentSkedMap[filterKey];

      if (!currentSked) {
        this.mainLoading$.next(true);
      } else {
        this.allTasks$.next(currentSked?.tasks);
        this.headerLoading$.next(true);
      }
    });
  }

  handleProjectSelected(skedContext: SkedContext, skedProjectsMap: Record<string, Record<string, Sked>>) {
    const filterKey = skedContext.filter === 'expired' ? 'expired' : 'today';
    const projectSked = skedProjectsMap[skedContext.projectId]?.[filterKey];

    if (!projectSked) {
      this.mainLoading$.next(true);
    } else {
      this.allTasks$.next(projectSked?.tasks);
      this.headerLoading$.next(true);
    }
  }

  getCurrentSked(expired: boolean) {
    this.store$.dispatch(
      new GetCurrentSkedAction({
        expired,
        onSuccess: () => this.onGetCurrentSkedSuccess(),
        onFailure: (err: HttpErrorResponse) => this.onGetSkedFailure(err),
      })
    );
  }

  getCurrentSkedProject(projectId: string, expired: boolean) {
    this.store$.dispatch(
      new GetCurrentSkedProjectAction({
        projectId,
        expired,
        onSuccess: () => this.onGetSkedProjectSuccess(),
        onFailure: (err: HttpErrorResponse) => this.onGetSkedFailure(err),
      })
    );
  }

  onGetCurrentSkedSuccess() {
    this.currentSked$.pipe(withLatestFrom(this.skedContext$), take(1)).subscribe(([skedMap, skedContext]) => {
      const filterKey = skedContext.filter === 'expired' ? 'expired' : 'today';
      this.allTasks$.next(skedMap[filterKey]?.tasks);
    });
    this.clearLoading();
  }

  onGetSkedProjectSuccess() {
    this.skedProjectsMap$
      .pipe(withLatestFrom(this.skedContext$), take(1))
      .subscribe(([skedProjectsMap, skedContext]) => {
        const filterKey = skedContext.filter === 'expired' ? 'expired' : 'today';
        this.allTasks$.next(skedProjectsMap[skedContext.projectId]?.[filterKey]?.tasks);
      });
    this.clearLoading();
  }

  onGetSkedFailure(err: HttpErrorResponse) {
    this.clearLoading();
    this.activeTasks$.next([]);
    this.filteredTasks$.next([]);
    this.messageService.add(err.error || 'There was an error getting the Sked');
  }

  subscribeToActiveUser(): Subscription {
    return this.user$
      .pipe(
        filter(u => !!u),
        take(1)
      )
      .subscribe(user => {
        // This is to refresh the active user in case lens has changed
        this.store$.dispatch(new GetActiveUserAction({}));
      });
  }

  subscribeToGetCurrentSkedInterval() {
    const timerOffset = (61 - getNowSecondsOffset()) * 1000;
    this.unsubscribeFromGetCurrentSkedInterval();
    this.getCurrentSkedDataInterval = timer(timerOffset, this.getFrequency * 1000).subscribe(() => {
      this.getCurrentSkedData();
    });
  }

  unsubscribeFromGetCurrentSkedInterval() {
    if (this.getCurrentSkedDataInterval) {
      this.getCurrentSkedDataInterval.unsubscribe();
    }
  }

  clearLoading() {
    this.mainLoading$.next(false);
    this.headerLoading$.next(false);
  }

  subscribeToTasks(): Subscription {
    return this.allTasks$.pipe(withLatestFrom(this.user$)).subscribe(([tasks, user]) => {
      const filteredTasks = tasks?.filter(
        task =>
          (!task.ownerId || task.ownerId === user._id) &&
          task.status !== TaskStatuses.Completed &&
          task.status !== TaskStatuses.Cancelled
      );

      const activeTasks = tasks?.filter(
        task =>
          task.ownerId === user._id && (task.status === TaskStatuses.InProgress || task.status === TaskStatuses.OnHold)
      );

      this.filteredTasks$.next(filteredTasks);
      this.activeTasks$.next(activeTasks);
    });
  }

  private initSkedContextFromRoute(): void {
    this.subscriptions.add(
      this.activatedRoute.params.subscribe(params => {
        const projectId = params['projectId'];
        const type: 'sked' | 'project' = projectId ? 'project' : 'sked';
        const skedContext: SkedContext = {
          type,
          projectId: projectId || '',
          preventGetSkedData: false,
          filter: 'today',
        };
        this.store$.dispatch(new UpdateSkedContextAction({skedContext}));
      })
    );
  }

  onChangeSkedContext(
    type: 'sked' | 'project',
    id: string,
    preventGetSkedData: boolean = false,
    filter: 'today' | 'expired' | 'stats' = 'today'
  ) {
    this.filterTabIndex = filter === 'today' ? 0 : 1;
    this.skedContext$.pipe(take(1)).subscribe(s => {
      const projectId = id || s.projectId;
      const skedContext = {
        ...s,
        type,
        projectId,
        preventGetSkedData: preventGetSkedData && s.type === 'project',
        filter,
      };
      if (skedContext.type === 'project' && !skedContext.projectId) return;
      this.store$.dispatch(new UpdateSkedContextAction({skedContext}));
    });
  }

  onTaskClick(taskId: string) {
    this.currentSkedService.navigatingFromTaskList = true;
    this.router.navigate(['task', taskId], {
      relativeTo: this.activatedRoute,
      state: {fromTaskList: true},
    });
  }

  onStatusAction(data: {statusAction: TaskStatusActions; skedId: string; taskId: string}) {
    this.loadingTaskAction$.next(true);
    this.store$.dispatch(
      new CurrentSkedChangeTaskStatusAction({
        id: data.taskId,
        actionType: data.statusAction,
        onSuccess: () => this.onActionSuccess(),
        onFailure: () => this.onActionFailure(),
      })
    );
  }

  onActionSuccess() {
    this.getCurrentSkedData();
    this.loadingTaskAction$.next(false);
  }

  onActionFailure() {
    this.loadingTaskAction$.next(false);
    this.messageService.add(`There was a problem updating the task.`);
  }

  onBackClick() {
    this.animationState = 'leave';
    setTimeout(() => {
      this.router.navigate(['..'], {relativeTo: this.activatedRoute});
    }, 200);
  }

  onFinishedNewTask = () => {
    this.getCurrentSkedData();
  };
}
